import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { AppModalFooter, AppModalShell } from "../../components/app-ui.js";
import { Button } from "../../components/legacy-ui.js";
import { upsertMilkWeekPrice } from "../../utils/api.js";
import { parseEsVeNumber } from "../../utils/formatters.js";

const fmtLiters = (value) =>
  Number(value || 0).toLocaleString("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const priceText = (value) => {
  const amount = Number(value || 0);
  if (!amount) return "";
  return amount.toLocaleString("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const inputClass =
  "w-full min-w-[5.5rem] text-right text-sm bg-white border border-gray-300 rounded px-2 py-1.5 outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600/30 tabular-nums";

function buildDraft(providers) {
  return [...providers]
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "es"))
    .map((provider) => ({
      id: provider.id,
      name: provider.name || "",
      litersVaca: provider.litersVaca,
      litersBufalo: provider.litersBufalo,
      origCow: Number(provider.priceCow || 0),
      origBuf: Number(provider.priceBuffalo || 0),
      cow: priceText(provider.priceCow),
      buf: priceText(provider.priceBuffalo),
    }));
}

export default function WeekPricesDialog({
  open,
  onClose,
  providers = [],
  weekStart,
  weekEnd,
  weekLabel,
  onSaved,
}) {
  const [draft, setDraft] = useState([]);
  const [search, setSearch] = useState("");
  const [allCow, setAllCow] = useState("");
  const [allBuf, setAllBuf] = useState("");
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const searchRef = useRef(null);
  const inputRefs = useRef({});
  const wasOpen = useRef(false);

  useEffect(() => {
    if (open && !wasOpen.current) {
      setDraft(buildDraft(providers));
      setSearch("");
      setAllCow("");
      setAllBuf("");
      setStatus("");
      setSaving(false);
    }
    wasOpen.current = open;
  }, [open, providers]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== "f") return;
      event.preventDefault();
      searchRef.current?.focus();
      searchRef.current?.select();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return draft;
    return draft.filter((row) => row.name.toLowerCase().includes(term));
  }, [draft, search]);

  const setPrice = (id, field, value) => {
    setDraft((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
    setStatus("");
  };

  const formatField = (id, field) => {
    const row = draft.find((item) => item.id === id);
    if (!row) return;
    const raw = String(row[field] || "").trim();
    if (!raw) return;
    const parsed = parseEsVeNumber(raw);
    if (parsed == null || parsed < 0) return;
    setPrice(id, field, parsed ? priceText(parsed) : "");
  };

  const focusAt = (index, field) => {
    const row = visible[index];
    if (!row) return;
    const el = inputRefs.current[`${row.id}:${field}`];
    if (!el) return;
    el.focus();
    el.select();
    el.scrollIntoView({ block: "nearest" });
  };

  const onPriceKeyDown = (event, index, field) => {
    if (event.key !== "Enter" && event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    if (event.key === "ArrowUp") {
      focusAt(index - 1, field);
      return;
    }
    if (index + 1 < visible.length) {
      focusAt(index + 1, field);
      return;
    }
    if (event.key === "Enter") focusAt(0, field);
  };

  const readPrice = (raw, label) => {
    const text = String(raw || "").trim();
    if (!text) return 0;
    const value = parseEsVeNumber(text);
    if (value == null || value < 0) {
      toast.error(`Precio inválido en ${label}.`, { position: "top-center" });
      return null;
    }
    return value;
  };

  const applyAll = () => {
    const cowRaw = allCow.trim();
    const bufRaw = allBuf.trim();
    if (!cowRaw && !bufRaw) {
      toast.error("Escribe el precio de vaca, el de búfala, o ambos.", { position: "top-center" });
      return;
    }
    const cow = cowRaw ? readPrice(cowRaw, "vaca") : null;
    if (cowRaw && cow == null) return;
    const buf = bufRaw ? readPrice(bufRaw, "búfala") : null;
    if (bufRaw && buf == null) return;
    if (visible.length === 0) {
      toast.error("Ningún proveedor coincide con la búsqueda.", { position: "top-center" });
      return;
    }
    const ids = new Set(visible.map((row) => row.id));
    setDraft((prev) =>
      prev.map((row) => {
        if (!ids.has(row.id)) return row;
        return {
          ...row,
          cow: cow == null ? row.cow : cow ? priceText(cow) : "",
          buf: buf == null ? row.buf : buf ? priceText(buf) : "",
        };
      }),
    );
    setStatus(`Precio copiado en ${visible.length} proveedores. Falta guardar.`);
  };

  const save = async () => {
    const updates = [];
    for (const row of draft) {
      const cow = readPrice(row.cow, row.name);
      if (cow == null) return;
      const buf = readPrice(row.buf, row.name);
      if (buf == null) return;
      const prices = {};
      if (Math.abs(cow - row.origCow) >= 0.0001) prices.priceCow = cow;
      if (Math.abs(buf - row.origBuf) >= 0.0001) prices.priceBuffalo = buf;
      if (Object.keys(prices).length > 0) {
        updates.push({ provider: row.id, ...prices });
      }
    }
    if (updates.length === 0) {
      setStatus("No hay cambios que guardar.");
      return;
    }

    setSaving(true);
    setStatus("Guardando…");
    try {
      await Promise.all(
        updates.map((item) =>
          upsertMilkWeekPrice({
            provider: item.provider,
            weekStart,
            weekEnd,
            ...(item.priceCow !== undefined ? { priceCow: item.priceCow } : {}),
            ...(item.priceBuffalo !== undefined ? { priceBuffalo: item.priceBuffalo } : {}),
          }),
        ),
      );
      toast.success(`Precios guardados en ${updates.length} proveedores.`, { position: "top-center" });
      if (onSaved) await onSaved();
      onClose();
    } catch (error) {
      console.error(error);
      setStatus("No se pudo guardar.");
      toast.error(error.response?.data?.error || "No se pudieron guardar los precios.", {
        position: "top-center",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppModalShell
      open={open}
      onClose={saving ? undefined : onClose}
      title="Ajustar precios"
      widthClass="max-w-3xl w-full"
      bodyScroll={false}
      footer={
        <AppModalFooter>
          <span className="text-sm text-gray-500 min-h-[1.25rem]">{status}</span>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="secondary" className="w-full sm:w-auto" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button className="w-full sm:w-auto" onClick={save} disabled={saving}>
              {saving ? "Guardando…" : "Guardar precios"}
            </Button>
          </div>
        </AppModalFooter>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 pb-3 border-b border-gray-100">
        <p className="text-sm text-gray-500">
          Precios de esta semana. Escribe vaca y búfala de cada proveedor, o usa «Aplicar a la lista»
          para copiar un precio en todos los que se ven. Enter baja al siguiente.
        </p>
        <p className="text-sm font-semibold text-gray-800 mt-2">{weekLabel || "Semana actual"}</p>
        <div className="mt-3 flex flex-wrap items-end gap-2 rounded-lg border border-gray-200 bg-[#f4f7f5] p-3">
          <span className="text-sm font-medium text-gray-800 pb-1.5">Aplicar a la lista</span>
          <label className="text-xs text-gray-600">
            Vaca
            <input
              type="text"
              inputMode="decimal"
              value={allCow}
              onChange={(event) => setAllCow(event.target.value)}
              className={`${inputClass} mt-1 w-24`}
            />
          </label>
          <label className="text-xs text-gray-600">
            Búfala
            <input
              type="text"
              inputMode="decimal"
              value={allBuf}
              onChange={(event) => setAllBuf(event.target.value)}
              className={`${inputClass} mt-1 w-24`}
            />
          </label>
          <Button variant="secondary" size="sm" onClick={applyAll} disabled={saving}>
            Aplicar
          </Button>
        </div>
        <input
          ref={searchRef}
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              setSearch("");
              event.currentTarget.blur();
            }
          }}
          placeholder="Buscar proveedor (Ctrl+F)"
          className="mt-3 w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-800 bg-white outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600/30"
        />
      </div>

      <div className="hidden sm:grid shrink-0 sm:grid-cols-[minmax(0,1fr)_9.5rem_7rem_7rem] gap-2 px-1 pt-3 pb-1 text-xs font-semibold text-gray-500">
        <span>Proveedor</span>
        <span className="text-right">Litros</span>
        <span className="text-right">Vaca $</span>
        <span className="text-right">Búfala $</span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
      {visible.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-400">
          {search.trim() ? "Ningún proveedor coincide con la búsqueda." : "No hay proveedores."}
        </p>
      ) : (
        <div className="divide-y divide-gray-100">
          {visible.map((row, index) => (
            <div
              key={row.id}
              className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_9.5rem_7rem_7rem] gap-2 sm:items-center py-2.5"
            >
              <div className="min-w-0">
                <p className="font-medium text-gray-900 truncate">{row.name}</p>
                <p className="text-xs text-gray-500 sm:hidden">
                  V {fmtLiters(row.litersVaca)} · B {fmtLiters(row.litersBufalo)}
                </p>
              </div>
              <p className="hidden sm:block text-xs text-gray-500 text-right tabular-nums">
                V {fmtLiters(row.litersVaca)} · B {fmtLiters(row.litersBufalo)}
              </p>
              <label className="text-xs text-gray-500 sm:text-right">
                <span className="sm:sr-only">Vaca</span>
                <input
                  ref={(el) => {
                    inputRefs.current[`${row.id}:cow`] = el;
                  }}
                  type="text"
                  inputMode="decimal"
                  aria-label={`Precio vaca de ${row.name}`}
                  value={row.cow}
                  disabled={saving}
                  onChange={(event) => setPrice(row.id, "cow", event.target.value)}
                  onBlur={() => formatField(row.id, "cow")}
                  onKeyDown={(event) => onPriceKeyDown(event, index, "cow")}
                  className={inputClass}
                />
              </label>
              <label className="text-xs text-gray-500 sm:text-right">
                <span className="sm:sr-only">Búfala</span>
                <input
                  ref={(el) => {
                    inputRefs.current[`${row.id}:buf`] = el;
                  }}
                  type="text"
                  inputMode="decimal"
                  aria-label={`Precio búfala de ${row.name}`}
                  value={row.buf}
                  disabled={saving}
                  onChange={(event) => setPrice(row.id, "buf", event.target.value)}
                  onBlur={() => formatField(row.id, "buf")}
                  onKeyDown={(event) => onPriceKeyDown(event, index, "buf")}
                  className={inputClass}
                />
              </label>
            </div>
          ))}
        </div>
      )}
      </div>
      </div>
    </AppModalShell>
  );
}
